import { useCallback } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { randomId } from '@mui/x-data-grid-generator';

import { setSnackbar } from 'shared/store/slices/snackBarSlice';
import { deleteHBL as deleteOimHBL } from 'shared/store/slices/oim-hblUpdateSlice';
import { deleteContainer as deleteOimContainer } from 'shared/store/slices/oim-containerUpdateSlice';
import { deleteHBL as deleteOexHBL } from 'shared/store/slices/oex-hblUpdateSlice';
import { deleteContainer as deleteOexContainer } from 'shared/store/slices/oex-containerUpdateSlice';
import { deleteHBL as deleteAimHBL } from 'shared/store/slices/aim-hblUpdateSlice';
import { deleteContainer as deleteAimContainer } from 'shared/store/slices/aim-containerUpdateSlice';
import { deleteHBL as deleteAexHBL } from 'shared/store/slices/aex-hblUpdateSlice';
import { deleteContainer as deleteAexContainer } from 'shared/store/slices/aex-containerUpdateSlice';

import { createSocketData, updateSocketData, deleteSocketData } from 'shared/lib/fetchSocket';
import { formatDateToDateString } from 'shared/lib/dateFormatter';

/** 문서에서 실제 HBL을 받아온 뒤, 담당자가 손대지 않은 껍데기 탭을 화면에서 걷어낼 때 쓴다. */
const DELETE_HBL_ACTIONS_BY_TAB = {
    0: deleteOimHBL,
    1: deleteOexHBL,
    2: deleteAimHBL,
    3: deleteAexHBL,
};
const DELETE_CNTR_ACTIONS_BY_TAB = {
    0: deleteOimContainer,
    1: deleteOexContainer,
    2: deleteAimContainer,
    3: deleteAexContainer,
};

/** 운송건을 등록할 때 자동으로 붙는, 담당자가 아직 아무것도 채우지 않은 HBL 탭인지 판단한다.
 *  번호도 없고 컨테이너 내용도 없으면 껍데기이므로 문서 내용으로 대체해도 잃을 것이 없다. */
function isHblEmpty(hbl, containerList, tabConfig, tabValue) {
    const fkHbl = tabConfig.keyName[1];
    const isOcean = tabValue === 0 || tabValue === 1;
    const numberVal = isOcean ? hbl?.hbl : hbl?.hawb;
    if (numberVal != null && String(numberVal).trim() !== '') return false;

    const containers = (containerList || []).filter((c) => c[fkHbl] === hbl?.id);
    if (containers.length === 0) return true;
    const hasNonEmpty = containers.some((c) => {
        if (isOcean)
            return (
                (c?.cntr_number != null && String(c.cntr_number).trim() !== '') ||
                (c?.volume != null && String(c.volume).trim() !== '')
            );
        return (
            (c?.alias != null && String(c.alias).trim() !== '') ||
            (c?.quantity != null && c.quantity !== 0) ||
            (c?.il_opus_vessel_id != null && String(c.il_opus_vessel_id).trim() !== '')
        );
    });
    return !hasNonEmpty;
}

/** 문서에서 읽어 운송건 본문에 채워줄 항목 목록. 해상은 선적항·양하항·최종도착지·선박·MBL,
 *  항공은 출발공항·도착공항·최종도착지·MAWB를 쓰고 출항일·도착일은 양쪽 공통이다.
 *  운송 모드가 다른 항목(해상 건의 MAWB 등)은 화면에 아예 없는 칸이라 보내지 않는다 —
 *  보내면 저장되지도 않으면서 담당자의 History에 있지도 않은 변경으로 남는다. */
const MAIN_ROW_DOCUMENT_KEYS = [
    { formKey: 'il_location_id_pol', parsedKey: 'pol', isLocation: true, mode: 'ocean' },
    { formKey: 'il_location_id_pod', parsedKey: 'pod', isLocation: true, mode: 'ocean' },
    { formKey: 'il_location_id_fpod', parsedKey: 'fpod', isLocation: true, mode: 'ocean' },
    { formKey: 'il_location_id_aol', parsedKey: 'aol', isLocation: true, mode: 'air' },
    { formKey: 'il_location_id_aod', parsedKey: 'aod', isLocation: true, mode: 'air' },
    { formKey: 'il_location_id_faod', parsedKey: 'faod', isLocation: true, mode: 'air' },
    { formKey: 'il_opus_vessel_id', parsedKey: 'vessel', isLocation: false, mode: 'ocean' },
    { formKey: 'mbl', parsedKey: 'mbl', isLocation: false, mode: 'ocean' },
    { formKey: 'mawb', parsedKey: 'mawb', isLocation: false, mode: 'air' },
    { formKey: 'etd', parsedKey: 'etd', isLocation: false },
    { formKey: 'eta', parsedKey: 'eta', isLocation: false },
];

/** 문서에서 읽은 값을 한 칸에 그대로 넣을 수 있는 단일 값으로 정리한다. */
function toScalar(value) {
    if (value == null) return null;
    if (typeof value === 'object') {
        // 출항일·도착일은 달력에 적힌 날짜라 시간대를 붙이지 않는다 — 붙이면 한국에서 입력한
        // 날짜가 캘리포니아 담당자 화면에서 하루 앞당겨 보인다.
        if (value instanceof Date) return formatDateToDateString(value);
        return null;
    }
    return value;
}

/** 문서에서 읽은 항구·공항을, 담당자가 자동완성에서 직접 고를 때와 같은 형태로 만든다.
 *  이 칸들은 이름까지 함께 저장해 두고 그 이름을 화면에 보여주므로, 번호만 넣으면
 *  선적항 칸에 항구명 대신 숫자가 뜬다. 등록된 항구와 매칭되지 않아도 문서에 적힌
 *  이름은 남겨, 담당자가 무엇이 읽혔는지 보고 손으로 고칠 수 있게 한다. */
function toLocationObject(parsedVal) {
    if (parsedVal == null) return null;
    if (typeof parsedVal === 'object') {
        if (parsedVal.id == null && parsedVal.name == null && parsedVal.code == null) return null;
        return {
            id: parsedVal.id ?? null,
            name: parsedVal.name ?? parsedVal.code ?? '',
            code: parsedVal.code ?? parsedVal.name ?? '',
            country: parsedVal.country ?? '',
        };
    }
    const text = String(parsedVal).trim();
    if (text === '') return null;
    return { id: null, name: text, code: text, country: '' };
}

/** MBL·MAWB 문서에서 읽은 내용을 운송건 본문의 칸별 수정 목록으로 바꾼다.
 *  담당자가 칸을 하나씩 고쳐 넣는 것과 같은 형태이며, 문서에 없던 항목은 건드리지 않아
 *  기존에 입력해 둔 값이 빈 값으로 덮이지 않게 한다. */
function buildMainRowCellUpdates(mblData, isOcean) {
    const parsedData = mblData?.parsedData ?? mblData;
    if (!parsedData || typeof parsedData !== 'object') return [];
    const mode = isOcean ? 'ocean' : 'air';
    const updates = [];
    MAIN_ROW_DOCUMENT_KEYS.forEach(({ formKey, parsedKey, isLocation, mode: keyMode }) => {
        if (keyMode && keyMode !== mode) return;
        let parsedVal = parsedData[parsedKey];
        // 항공 문서를 읽었는데 운송장 번호가 해상 항목 이름으로 담겨 온 경우에도 MAWB 칸을 채운다.
        if (formKey === 'mawb' && (parsedVal === undefined || parsedVal === null)) parsedVal = parsedData.mbl;
        if (parsedVal === undefined || parsedVal === null) return;
        if (isLocation) {
            const loc = toLocationObject(parsedVal);
            if (loc) updates.push({ key: formKey, value: loc });
            return;
        }
        if (formKey === 'il_opus_vessel_id') {
            const isObj = typeof parsedVal === 'object' && parsedVal !== null;
            const v = isObj ? parsedVal.name ?? parsedVal.id : String(parsedVal ?? '');
            if (v !== null && v !== undefined && v !== '') updates.push({ key: formKey, value: toScalar(v) ?? v });
            return;
        }
        const v = toScalar(parsedVal);
        if (v !== null && v !== undefined && v !== '') updates.push({ key: formKey, value: v });
    });
    return updates;
}

/** 문서에서 읽은 컨테이너(해상) 또는 분할 화물(항공) 한 건을 새 행으로 만든다.
 *  해상은 컨테이너 번호와 규격, 수출은 씰 번호까지, 항공은 분할 이름·편명·수량·단위·중량을 채운다. */
function buildContainerPayload(tabConfig, isOcean, isOex, fkHbl, hblId, c) {
    const base = { ...tabConfig.newCNTR, id: randomId(), [fkHbl]: hblId };
    if (isOcean) {
        return {
            ...base,
            cntr_number: c.number ?? c.cntr_number ?? '',
            volume: c.size ?? c.volume ?? '',
            ...(isOex && { seal_number: c.seal_number ?? '' }),
        };
    }
    return {
        ...base,
        alias: c.alias ?? '',
        il_opus_vessel_id: c.flight ?? c.il_opus_vessel_id ?? '',
        quantity: c.volume != null ? Number(c.volume) : 0,
        il_opus_volume_unit_id: c.unit ?? c.il_opus_volume_unit_id ?? '',
        weight: c.weight != null ? Number(c.weight) : 0,
    };
}

/** 방금 추가한 HBL 탭이 화면에 나타날 때까지 기다린 뒤 그 행을 돌려준다.
 *  컨테이너는 어느 HBL에 속하는지 지정해야 만들 수 있고, 그 기준이 되는 번호는 저장이 끝난
 *  뒤에야 정해진다 — 담당자가 HBL을 손으로 추가한 다음 컨테이너를 넣는 순서와 같다.
 *  같은 운송건을 동료가 동시에 고칠 수 있으므로, 문서에서 읽은 HBL 번호까지 맞는 탭만 집는다. */
function waitForNewChildRow({ store, dataKey, fkKey, parentId, existingIds, matchKey, matchVal, timeoutMs = 8000 }) {
    return new Promise((resolve) => {
        const findNew = () => {
            const rows = store.getState()[dataKey] || [];
            return rows.find(
                (r) =>
                    r[fkKey] === parentId &&
                    !existingIds.has(r.id) &&
                    (!matchVal || String(r[matchKey] ?? '') === String(matchVal))
            );
        };
        const already = findNew();
        if (already) {
            resolve(already);
            return;
        }
        let done = false;
        const finish = (val) => {
            if (done) return;
            done = true;
            unsubscribe();
            clearTimeout(timer);
            resolve(val);
        };
        const unsubscribe = store.subscribe(() => {
            const found = findNew();
            if (found) finish(found);
        });
        const timer = setTimeout(() => finish(null), timeoutMs);
    });
}

/** HBL·컨테이너 행 하나를 추가하고, 저장이 끝났는지 여부를 돌려준다.
 *  문서 한 장이 여러 행을 만들기 때문에, 앞 행의 저장이 끝난 뒤 다음 행으로 넘어가야
 *  화면에 문서에 적힌 순서대로 쌓인다. */
function createChild(dispatch, tabValue, entity, reducer, payload) {
    return new Promise((resolve) => {
        createSocketData(dispatch, tabValue, 'row', entity, reducer, payload, () => resolve(true), () => resolve(false));
    });
}

/** 운송건 본문의 한 칸을 고치고, 저장이 끝났는지 여부를 돌려준다.
 *  실패한 칸은 각자 오류 안내가 이미 떠 있으므로, 여기서는 성공 여부만 세어 마지막에 종합한다. */
function updateCell(dispatch, tabValue, entity, reducer, payload) {
    return new Promise((resolve) => {
        updateSocketData(dispatch, tabValue, 'cell', entity, reducer, payload, () => resolve(true), () => resolve(false));
    });
}

/**
 * 이미 등록해 둔 운송건을 열고 선적 서류를 올렸을 때, 읽어낸 내용을 그 운송건에 채워준다.
 *
 * 담당자가 하는 일은 "기존 건에 서류 내용을 반영"하는 것이지 새 건을 만드는 것이 아니다.
 * 신규 등록 경로로 보내면 참조번호를 매번 새로 따려 해서 이미 할당된 번호라는 오류가 나고,
 * 변경 이력에도 등록 기록이 중복으로 쌓인다. 그래서 본문은 칸을 하나씩 고치는 방식으로,
 * HBL과 컨테이너는 담당자가 손으로 추가할 때와 똑같이 하위 행을 더하는 방식으로 반영한다.
 *
 * @param {object} mainRow - 담당자가 열어 둔 운송건
 * @param {object|null} tabConfig - 운송 모드별 화면 설정 (트럭 운송은 없을 수 있음)
 * @param {number} tabValue - 운송 모드 (0~4)
 * @param {function} onCloseDialog - 반영이 끝나면 업로드 창을 닫는 콜백
 * @returns {function} handleApplyParsedData(applyPayload)
 */
export default function useDocumentApplyToDetail(mainRow, tabConfig, tabValue, onCloseDialog) {
    const dispatch = useDispatch();
    const store = useStore();

    const handleApplyParsedData = useCallback(
        async (applyPayload) => {
            const isTrucking = tabValue === 4;
            if (isTrucking) {
                onCloseDialog?.();
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Document apply for Trucking is not supported in this flow.',
                        severity: 'info',
                    })
                );
                return;
            }

            if (!tabConfig) {
                onCloseDialog?.();
                return;
            }

            const mblData = applyPayload?.mblData ?? null;
            const hblList = applyPayload?.hblList ?? [];
            const isOcean = tabValue === 0 || tabValue === 1;
            const isOex = tabValue === 1;
            const label = isOcean ? 'HBL(s)' : 'HAWB(s)';

            const fkToMain = tabConfig.keyName[0];
            const fkHbl = tabConfig.keyName[1];
            const hblNumberKey = isOcean ? 'hbl' : 'hawb';

            const mainCellUpdates = mblData ? buildMainRowCellUpdates(mblData, isOcean) : [];

            if (mainCellUpdates.length === 0 && hblList.length === 0) {
                onCloseDialog?.();
                dispatch(setSnackbar({ open: true, message: 'No data to apply.', severity: 'info' }));
                return;
            }

            // 업로드 창은 곧바로 닫히고 반영은 몇 초간 이어지므로, 담당자가 멈춘 것으로 오해하지 않게 알린다.
            dispatch(setSnackbar({ open: true, message: 'Applying parsed document data…', severity: 'info' }));

            // 서류를 올리기 전부터 화면에 있던 HBL 탭 목록. 3)에서 껍데기 탭을 걷어낼 때 이 목록만
            // 대상으로 삼아, 방금 문서로 만든 탭이 함께 지워지는 일을 막는다.
            const preExistingHblIds = new Set((store.getState()[tabConfig.houseData] || []).map((r) => r.id));

            try {
                // 1) 운송건 본문의 칸을 하나씩 고친다 — 담당자가 그리드에서 직접 고치는 것과 같은 방식이다.
                let appliedCells = 0;
                let failedCount = 0;
                for (const { key, value } of mainCellUpdates) {
                    const ok = await updateCell(dispatch, tabValue, tabConfig.tableName[0], tabConfig.updateReducer[0], {
                        id: mainRow.id,
                        key,
                        value,
                        table_id: [],
                    });
                    if (ok) appliedCells += 1;
                    else failedCount += 1;
                }

                // 2) 서류에서 읽은 HBL을 탭으로 추가하고, 그 탭이 화면에 뜬 다음 컨테이너를 붙인다 —
                //    담당자가 HBL을 추가한 뒤 컨테이너를 넣는 순서와 같다.
                let addedCount = 0;
                for (const item of hblList) {
                    const pd = item.parsedData || item;
                    const numberVal = pd?.hbl ?? pd?.hawb ?? pd?.[hblNumberKey] ?? '';
                    const newHBL = {
                        ...tabConfig.newHBL,
                        id: randomId(),
                        [fkToMain]: mainRow.id,
                        [hblNumberKey]: numberVal,
                    };

                    const existingHblIds = new Set((store.getState()[tabConfig.houseData] || []).map((r) => r.id));
                    const created = await createChild(
                        dispatch,
                        tabValue,
                        tabConfig.tableName[1],
                        tabConfig.addReducer[1],
                        newHBL
                    );
                    if (!created) {
                        failedCount += 1;
                        continue;
                    }
                    addedCount += 1;

                    const containerList = isOcean ? pd?.containers : pd?.aliases;
                    if (!Array.isArray(containerList) || containerList.length === 0) continue;

                    const newHblRow = await waitForNewChildRow({
                        store,
                        dataKey: tabConfig.houseData,
                        fkKey: fkToMain,
                        parentId: mainRow.id,
                        existingIds: existingHblIds,
                        matchKey: hblNumberKey,
                        matchVal: numberVal,
                    });
                    if (!newHblRow) {
                        failedCount += containerList.length;
                        continue;
                    }

                    // 담당자가 서류와 화면을 위에서부터 대조할 수 있도록 서류에 적힌 순서로 한 건씩 만든다.
                    for (const c of containerList) {
                        const ok = await createChild(
                            dispatch,
                            tabValue,
                            tabConfig.tableName[2],
                            tabConfig.addReducer[2],
                            buildContainerPayload(tabConfig, isOcean, isOex, fkHbl, newHblRow.id, c)
                        );
                        if (!ok) failedCount += 1;
                    }
                }

                // 3) 서류로 실제 HBL이 들어왔으니, 운송건 등록 때 자동으로 붙었던 빈 탭은 걷어낸다 —
                //    담당자가 빈 탭과 실제 HBL을 함께 보며 어느 쪽이 진짜인지 헷갈리지 않게 한다.
                if (addedCount > 0 && tabValue >= 0 && tabValue <= 3) {
                    const state = store.getState();
                    const hblData = state[tabConfig.houseData] || [];
                    const containerData = state[tabConfig.containerData] || [];
                    const deleteHblAction = DELETE_HBL_ACTIONS_BY_TAB[tabValue];
                    const deleteCntrAction = DELETE_CNTR_ACTIONS_BY_TAB[tabValue];
                    const mainHbls = hblData.filter(
                        (h) => h[fkToMain] === mainRow.id && preExistingHblIds.has(h.id)
                    );
                    const emptyHbls = mainHbls.filter((hbl) => isHblEmpty(hbl, containerData, tabConfig, tabValue));

                    const runDeletes = (index, onAllDone) => {
                        if (index >= emptyHbls.length) {
                            onAllDone?.();
                            return;
                        }
                        const hbl = emptyHbls[index];
                        const containers = (containerData || []).filter((c) => c[fkHbl] === hbl.id);
                        const deleteNextContainer = (cIdx, onContainersDone) => {
                            if (cIdx >= containers.length) {
                                onContainersDone?.();
                                return;
                            }
                            deleteSocketData(
                                dispatch,
                                tabValue,
                                'row',
                                tabConfig.tableName[2],
                                tabConfig.deleteReducer[2],
                                containers[cIdx].id,
                                () => {
                                    dispatch(deleteCntrAction(containers[cIdx].id));
                                    deleteNextContainer(cIdx + 1, onContainersDone);
                                },
                                () => deleteNextContainer(cIdx + 1, onContainersDone)
                            );
                        };
                        deleteNextContainer(0, () => {
                            deleteSocketData(
                                dispatch,
                                tabValue,
                                'row',
                                tabConfig.tableName[1],
                                tabConfig.deleteReducer[1],
                                hbl.id,
                                () => {
                                    dispatch(deleteHblAction(hbl.id));
                                    runDeletes(index + 1, onAllDone);
                                },
                                () => runDeletes(index + 1, onAllDone)
                            );
                        });
                    };
                    runDeletes(0);
                }

                // 4) 반영되지 않은 항목이 있으면 성공으로 덮지 않고 알린다 — 담당자가 저장됐다고
                //    믿고 넘어가면 빠진 값이 그대로 운영에 흘러간다.
                if (failedCount > 0 && appliedCells === 0 && addedCount === 0) {
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: 'Failed to apply document data. No changes were made.',
                            severity: 'error',
                        })
                    );
                } else if (failedCount > 0) {
                    dispatch(
                        setSnackbar({
                            open: true,
                            message: `Document applied with ${failedCount} error(s) — please review the shipment.`,
                            severity: 'warning',
                        })
                    );
                } else {
                    const msg =
                        addedCount > 0
                            ? `Main shipment updated and ${addedCount} ${label} added from document(s).`
                            : 'Main shipment updated from document(s).';
                    dispatch(setSnackbar({ open: true, message: msg, severity: 'success' }));
                }
            } catch (error) {
                console.error('Document apply failed:', error);
                dispatch(
                    setSnackbar({
                        open: true,
                        message: 'Failed to apply document data — please review the shipment.',
                        severity: 'error',
                    })
                );
            } finally {
                onCloseDialog?.();
            }
        },
        [dispatch, mainRow, tabConfig, tabValue, store, onCloseDialog]
    );

    return handleApplyParsedData;
}
